import { Input, Form, Modal, InputNumber, Select } from 'antd';
import {
  closeModal,
  CreateComplexModalVisibleSelector,
} from './CreateComplexModalSlice';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import {
  createComplex,
  CreateComplexModalDataSelector,
} from './CreateComplexModalSlice';
import * as S from './CreateComplexModal.styled';
import { IComplex, IRegion, IOrganization } from 'interfaces/main';
import { unwrapResult } from '@reduxjs/toolkit';
import { JSX } from 'react/jsx-runtime';
import { OrganizationsListItemsSelector } from 'features/OrganizationsList/OrganizationsListSlice';
import { IsOwnerSelector } from 'features/User/UserSlice';
import dayjs from 'dayjs';
import { getQuart } from 'utils/help';
import { LibrariesSelector } from 'features/Libraries/LibrariesSlice';

const { Option } = Select;
const { TextArea } = Input;

const CreateComplexModal = (): JSX.Element => {
  const dispatch = useAppDispatch();

  // Инициализация формы
  const [form] = Form.useForm();

  const data = useAppSelector(CreateComplexModalDataSelector);
  const isOwner = useAppSelector(IsOwnerSelector);

  // Закрытие окна
  const onClose = () => {
    form.resetFields();
    dispatch(closeModal());
  };

  // Организации
  const organizations = useAppSelector(OrganizationsListItemsSelector);

  // Отправка формы
  const onSubmit = (complex: IComplex) => {
    dispatch(
      createComplex({
        ...complex,
      }),
    )
      .then(unwrapResult)
      .then((org: IComplex) => {
        if (org.id) {
          form.resetFields();
        }
      });
  };

  // Видимость модального окна
  const isVisible = useAppSelector(CreateComplexModalVisibleSelector);

  // Словари
  const { regions: allRegions } = useAppSelector(LibrariesSelector);

  return (
    <Modal
      open={isVisible}
      title="Create new complex"
      okText="Create"
      cancelText="Cancel"
      onCancel={onClose}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            onSubmit(values);
          })
          .catch((info) => {
            console.log('Validate Failed:', info);
          });
      }}>
      <Form form={form} layout="vertical" name="groupForm" initialValues={data}>
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: 'Please input title' }]}>
          <Input type="text" name="title" placeholder="Dubai Properties" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <TextArea
            style={{ width: '100%' }}
            rows={2}
            placeholder="Dubai Properties is also committed to sustainability, exemplified by The Sustainable City, a development focused on minimizing ecological impact through renewable energy, green spaces, and sustainable living practices."
            maxLength={1000}
            name="description"
          />
        </Form.Item>
        <Form.Item name="description_en" label="Description (English)">
          <TextArea
            style={{ width: '100%' }}
            rows={2}
            placeholder="Dubai Properties is also committed to sustainability, exemplified by The Sustainable City, a development focused on minimizing ecological impact through renewable energy, green spaces, and sustainable living practices."
            maxLength={1000}
            name="description_en"
          />
        </Form.Item>
        <Form.Item name="description_ru" label="Description (Russian)">
          <TextArea
            style={{ width: '100%' }}
            rows={2}
            placeholder="Dubai Properties is also committed to sustainability, exemplified by The Sustainable City, a development focused on minimizing ecological impact through renewable energy, green spaces, and sustainable living practices."
            maxLength={1000}
            name="description_ru"
          />
        </Form.Item>
        <S.Line>
          <Form.Item
            name="longitude"
            style={{ width: '50%' }}
            label="Longitude">
            <InputNumber
              type="text"
              placeholder="55.31969"
              name="longitude"
              style={{ width: '100%' }}
              min={51}
              max={56.25}
            />
          </Form.Item>
          <Form.Item name="latitude" style={{ width: '50%' }} label="Latitude">
            <InputNumber
              type="text"
              placeholder="25.179711"
              name="latitude"
              style={{ width: '100%' }}
              min={22.5}
              max={26}
            />
          </Form.Item>
        </S.Line>
        <S.Line>
          <Form.Item
            name="year"
            style={{ width: '33%' }}
            label="Completion year"
            rules={[{ required: true, message: 'Please set year' }]}>
            <InputNumber
              type="text"
              placeholder={dayjs().year().toString()}
              name="year"
              style={{ width: '100%' }}
              min={2020}
              max={2030}
              step={1}
            />
          </Form.Item>
          <Form.Item
            name="quart"
            style={{ width: '33%' }}
            label="Completion quart"
            rules={[{ required: true, message: 'Please set quart' }]}>
            <InputNumber
              type="text"
              placeholder={getQuart(dayjs().month()).toString()}
              name="quart"
              style={{ width: '100%' }}
              min={1}
              max={4}
              step={1}
            />
          </Form.Item>
          <Form.Item name="rating" style={{ width: '33%' }} label="Rating">
            <InputNumber
              type="text"
              placeholder="0"
              name="rating"
              style={{ width: '100%' }}
              min={0}
              max={5}
              step={0.1}
            />
          </Form.Item>
        </S.Line>
        <S.Line>
          <Form.Item
            name="regionId"
            style={{ width: '50%' }}
            label="Region"
            rules={[{ required: true, message: 'Please select Region' }]}>
            <Select
              style={{ width: '100%' }}
              placeholder="Please select Region"
              disabled={!isOwner}>
              {allRegions.map((region: IRegion) => (
                <Option key={region.id} value={region.id}>
                  {region.name_en}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="organizationId"
            style={{ width: '50%' }}
            label="Organization"
            rules={[{ required: true, message: 'Please select organization' }]}>
            <Select
              style={{ width: '100%' }}
              placeholder="Please select"
              disabled={!isOwner}>
              {organizations.map((org: IOrganization) => (
                <Option key={org.id} value={org.id}>
                  {org.title}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </S.Line>
      </Form>
    </Modal>
  );
};

export default CreateComplexModal;
